import { useEffect } from 'react';

interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: string;
  jsonLd?: object[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applyJsonLd(items: object[] | undefined) {
  document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
  if (!items || items.length === 0) return;
  items.forEach((item, i) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

/**
 * 站内 SEO：设置标题、描述、关键词、Canonical、OG/Twitter 与 JSON-LD 结构化数据。
 */
export default function useSeo({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  jsonLd,
}: SeoOptions) {
  useEffect(() => {
    const base = (window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? '';
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${base}${canonicalPath === '/' ? '/' : canonicalPath}`;

    document.title = title;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', 'index, follow');
    upsertMeta('name', 'author', '韩漫精选榜');
    upsertMeta('name', 'theme-color', '#c94a24');

    upsertCanonical(canonicalUrl);

    upsertMeta('property', 'og:site_name', '韩漫精选榜');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:locale', 'zh_CN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    applyJsonLd(jsonLd);
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);
}